// @flow
import fp from "lodash/fp"
import { Map } from "immutable"
import { createSelector } from "reselect"

import type { Action } from "./actions"
import Price from "common/records/Price"
import type { State } from "booking/services/types"

export type InsurancePricing = {
  none: number,
  skysilver: ?number,
  skygold: ?number,
  get?: (e: string) => any,
}

export type Prices = {
  basePrice: ?number,
  adultsPrice: ?number,
  childrenPrice: ?number,
  infantsPrice: ?number,
  fees: Map, // FIXME: rewrite to { allFees: number, baseFare: number, merchantFee: number }
  gracePeriodFee: ?number,
  insurancePricing: InsurancePricing,
  holdBagsPricingCurrent: ?Map<string, number>, // FIXME: rewrite to object
  accepted: ?Map, // FIXME: rewrite to object??

  // FIXME: remove in next task?
  shameBagsPrice: ?number,
  shameFinalPriceInCurrency: ?number,
}

const DEFAULT_STATE: Prices = {
  basePrice: null,
  adultsPrice: null,
  childrenPrice: null,
  infantsPrice: null,
  fee: null,
  fees: Map(),
  gracePeriodFee: null,
  insurancePricing: {
    none: 0,
    skysilver: null,
    skygold: null,
  },
  holdBagsPricingCurrent: null, // holdBagsPricing which is updated during booking (only for debugging now)
  accepted: Map(), // AcceptedPrices
  shameBagsPrice: null,
  shameFinalPriceInCurrency: null,
}

const pricesReducer = (state: Prices = DEFAULT_STATE, action: Action): Prices => {
  switch (action.type) {
    case "SET_ACCEPTED":
      return fp.set("accepted", action.payload, state)
    case "LOAD_SUCCESS_PRICES":
      return fp.compose(
        fp.set("basePrice", action.payload.basePrice),
        fp.set("adultsPrice", action.payload.adultsPrice),
        fp.set("childrenPrice", action.payload.childrenPrice),
        fp.set("infantsPrice", action.payload.infantsPrice),
        fp.set("fees", action.payload.fees),
        fp.set("gracePeriodFee", action.payload.gracePeriodFee),
        fp.set("accepted", action.payload.accepted),
        fp.set("holdBagsPricingCurrent", action.payload.holdBagsPricingCurrent),
      )(state)
    case "UPDATE_SUCCESS_PRICES":
      return fp.compose(
        fp.set("fees", action.payload.fees),
        fp.set("basePrice", action.payload.basePrice),
        fp.set("adultsPrice", action.payload.adultsPrice),
        fp.set("childrenPrice", action.payload.childrenPrice),
        fp.set("infantsPrice", action.payload.infantsPrice),
        fp.set("accepted", action.payload.accepted),
      )(state)
    case "SAVE_SUCCESS_PRICES":
      return fp.compose(
        fp.set("shameBagsPrice", action.payload.shameBagsPrice),
        fp.set("shameFinalPriceInCurrency", action.payload.shameFinalPriceInCurrency),
      )(state)
    default:
      return state
  }
}

export default pricesReducer

// Data selectors
// ===
export const getPrices = (state: State) => state.reservation.prices

export const getBasePrice: $FlowFixMe = createSelector(
  getPrices,
  prices => new Price({ amount: prices.basePrice }),
)

export const getAdultsPrice: $FlowFixMe = createSelector(
  getPrices,
  prices => new Price({ amount: prices.adultsPrice }),
)

export const getChildrenPrice: $FlowFixMe = createSelector(
  getPrices,
  getAdultsPrice,
  (prices, adultsPrice) =>
    // If childrenPrice not loaded yet, use adult price https://go.kiwi.com/XI_04uq86
    prices.childrenPrice === 0 ? adultsPrice : new Price({ amount: prices.childrenPrice }),
)

// TODO: change once teens price is ready from be
// export const getTeensPrice = createSelector(
//   getPrices,
//   prices => new Price({ amount: prices.adultsPrice }),
// )

export const getInfantsPrice: $FlowFixMe = createSelector(
  getPrices,
  prices => new Price({ amount: prices.infantsPrice }),
)

export const getFees: $FlowFixMe = createSelector(
  getPrices,
  prices => prices.fees,
)
export const getGracePeriodFee: $FlowFixMe = createSelector(
  getPrices,
  prices => prices.gracePeriodFee,
)

export const getHoldBagsPricingCurrent: $FlowFixMe = createSelector(
  getPrices,
  prices => prices.holdBagsPricingCurrent,
)
export const getAccepted: $FlowFixMe = createSelector(
  getPrices,
  prices => prices.accepted,
)
export const getShameBagsPrice: $FlowFixMe = createSelector(
  getPrices,
  prices => prices.shameBagsPrice,
)
export const getShameFinalPriceInCurrency: $FlowFixMe = createSelector(
  getPrices,
  prices => prices.shameFinalPriceInCurrency,
)
